import React, { useState } from "react";
import WelocateFilterButton from "./WelocateFilterButton";
import WelocatePriceFilter from "./WelocatePriceFilter";
import WelocateTypeFilter from "./WelocateTypeFilter";
import WelocateBedroomFilter from "./WelocateBedroomFilter";
import WelocateAreaFilter from "./WelocateAreaFilter";
import WelocateStatusFilter from "./WelocateStatusFilter";
import WelocateEnergyLabelFilter from "./WelocateEnergyLabelFilter";
import WelocateUxExperienceFilter from "./WelocateUxExperienceFilter";
import "./WelocateFilters.css";

const MIN = 0;
const MAX = 2500000;

const formatPrice = (value) => {
  if (value >= 1000000) {
    const m = value / 1000000;
    return `${m % 1 === 0 ? m : m.toFixed(1)}M`;
  }
  return `${Math.round(value / 1000)}k`;
};

const WelocateFilters = ({
  onPriceApply,
  onTypeApply,
  onBedroomApply,
  onAreaApply,
  onStatusApply,
  onEnergyLabelApply,
  onUxExperienceApply,
  bedroomSelected = null,
  areaSelected = null,
  typeSelected = [],
  statusSelected = [],
  energyLabelsSelected = [],
  statusCounts = {},
  totalCount = 0,
  uxExperienceSelected = 1,
}) => {
  const [priceRange, setPriceRange] = useState([MIN, MAX]);
  const [appliedPriceRange, setAppliedPriceRange] = useState([MIN, MAX]);
  const [isPriceActive, setIsPriceActive] = useState(false);

  const isTypeActive = typeSelected.length > 0;

  const [openFilter, setOpenFilter] = useState(null);

  const makeOpenChange = (key) => (v) => setOpenFilter(v ? key : null);

  const handleStatusToggle = (value) => {
    const next = statusSelected.includes(value)
      ? statusSelected.filter((s) => s !== value)
      : [...statusSelected, value];
    onStatusApply?.(next);
  };

  const handleStatusReset = () => {
    onStatusApply?.([]);
  };

  const handlePriceApply = (min, max) => {
    const active = min !== null && (min !== MIN || max !== MAX);
    setIsPriceActive(active);
    setAppliedPriceRange(active ? [min, max] : [MIN, MAX]);
    onPriceApply(min, max);
  };

  const handleTypeToggle = (type) => {
    const next = typeSelected.includes(type)
      ? typeSelected.filter((t) => t !== type)
      : [...typeSelected, type];
    onTypeApply?.(next);
  };

  const handleBedroomSelect = (value) => {
    onBedroomApply?.(value);
  };

  const handleAreaSelect = (value) => {
    onAreaApply?.(value);
  };

  const handleUxExperienceSelect = (value) => {
    if (uxExperienceSelected === value) {
      setOpenFilter(null);
      return;
    }
    onUxExperienceApply?.(value);
    setOpenFilter(null);
  };

  const handleEnergyLabelToggle = (value) => {
    const next = energyLabelsSelected.includes(value)
      ? energyLabelsSelected.filter((l) => l !== value)
      : [...energyLabelsSelected, value];
    onEnergyLabelApply?.(next);
  };

  return (
    <div className="wl-filters">
      <WelocateFilterButton label="Status" sublabel={statusSelected.length > 0 ? statusSelected.length : null} active={statusSelected.length > 0} open={openFilter === "status"} onOpenChange={makeOpenChange("status")}>
        <WelocateStatusFilter
          selected={statusSelected}
          onToggle={handleStatusToggle}
          counts={statusCounts}
          totalCount={totalCount}
          onReset={handleStatusReset}
          onClose={() => setOpenFilter(null)}
        />
      </WelocateFilterButton>
      <WelocateFilterButton label="Price" sublabel={isPriceActive ? `${formatPrice(appliedPriceRange[0])} - ${formatPrice(appliedPriceRange[1])}` : null} active={isPriceActive} open={openFilter === "price"} onOpenChange={makeOpenChange("price")}>
        <WelocatePriceFilter
          range={priceRange}
          onRangeChange={setPriceRange}
          onApply={handlePriceApply}
          onClose={() => setOpenFilter(null)}
        />
      </WelocateFilterButton>
      <WelocateFilterButton label="Type" sublabel={typeSelected.length > 0 ? typeSelected.length : null} active={isTypeActive} open={openFilter === "type"} onOpenChange={makeOpenChange("type")}>
        <WelocateTypeFilter selected={typeSelected} onToggle={handleTypeToggle} />
      </WelocateFilterButton>
      <WelocateFilterButton label="Bedrooms" sublabel={bedroomSelected !== null ? `${bedroomSelected}+` : null} active={bedroomSelected !== null} open={openFilter === "bedrooms"} onOpenChange={makeOpenChange("bedrooms")}>
        <WelocateBedroomFilter selected={bedroomSelected} onSelect={handleBedroomSelect} />
      </WelocateFilterButton>
      <WelocateFilterButton label="Min m²" sublabel={areaSelected !== null ? `${areaSelected}+` : null} active={areaSelected !== null} open={openFilter === "area"} onOpenChange={makeOpenChange("area")}>
        <WelocateAreaFilter selected={areaSelected} onSelect={handleAreaSelect} />
      </WelocateFilterButton>
      <WelocateFilterButton label="Energy Label" sublabel={energyLabelsSelected.length > 0 ? energyLabelsSelected.length : null} active={energyLabelsSelected.length > 0} open={openFilter === "energyLabel"} onOpenChange={makeOpenChange("energyLabel")}>
        <WelocateEnergyLabelFilter selected={energyLabelsSelected} onToggle={handleEnergyLabelToggle} />
      </WelocateFilterButton>
      <WelocateFilterButton label="UX experience" sublabel={uxExperienceSelected} active open={openFilter === "uxExperience"} onOpenChange={makeOpenChange("uxExperience")}>
        <WelocateUxExperienceFilter selected={uxExperienceSelected} onSelect={handleUxExperienceSelect} />
      </WelocateFilterButton>
    </div>
  );
};

export default WelocateFilters;
